import React, { useRef } from 'react'
import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from 'theme/useTheme'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import useStore from 'reusable/mobx-store-utils'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { themeConfig }from '@gap/shared/theme'

export interface GapButtonTooltipProps {
   buttonText: string // text to display in the tooltip
   tooltip: React.ReactNode // tooltip content
}

export default observer(function GapButtonTooltip(props: GapButtonTooltipProps) {
   const localStore = useStore(() => ({
      popperState: new ModalState(),
   }))

   const theme = useTheme()
   const anchorRef = useRef<HTMLButtonElement>(null)
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

   return (
      <>
         <Button
            size='small'
            ref={anchorRef}
            onClick={localStore.popperState.open}
         >
            {props.buttonText}
         </Button>
         <Popper
            sx={{ zIndex: theme.zIndex.tooltip, maxWidth: isMobile ? '90%' : '338px' }}
            open={localStore.popperState.visible}
            anchorEl={anchorRef.current}
            placement={isMobile ? "bottom" : "right"}
            modifiers={[
               {
                  name: 'flip',
                  enabled: true,
                  options: {
                     fallbackPlacements: ['bottom'], // This tells Popper to flip to the bottom if right is not available
                     boundary: 'viewport',
                     padding: 8,
                  },
               },
               {
                  name: 'preventOverflow',
                  enabled: true,
                  options: {
                     altAxis: true, // Allows the Popper to switch to the opposite axis if there isn't enough space
                     altBoundary: true,
                     tether: false, // Allows the popper to detach from the anchor to fit into the viewport
                     boundary: 'viewport',
                     padding: 8,
                  },
               },
            ]}
         >
            <ClickAwayListener onClickAway={localStore.popperState.close}>
               <Paper sx={{ m: 2, p: 2, boxShadow: themeConfig.popperShadow, backgroundColor: theme.palette.gap.primary['50'] }}>
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'start' }}>
                     <Box sx={{ textAlign: 'left' }}>
                        {typeof props.tooltip === 'string' ? <Typography variant='body2' sx={{ flexGrow: 1, pr: 1 }}>{props.tooltip}</Typography> : props.tooltip}
                     </Box>
                     <IconButton sx={{ mt: -.5 }} size="small" onClick={localStore.popperState.close}>
                        <CloseIcon sx={{ fontSize: '20px', color: 'primary.light' }} />
                     </IconButton>
                  </Box>
               </Paper>
            </ClickAwayListener>
         </Popper>
      </>
   )
})